import type React from 'react';
import { type FC, useCallback, useMemo } from 'react';
import {
  MantineReactTable,
  type MRT_VisibilityState,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState
} from 'mantine-react-table';
import dayjs from 'dayjs';
import { UiButton, UiHStack, UiIconArrowRight, UiStack, uiStyles } from '@/lib/ui';
import { type UserTableRow } from '@/api/admin/user';
import { useNavigate } from 'react-router-dom';
import { generatePageUrl } from '@/app/pages';
import { useDataTableOptions } from '@/registration/hook/useDataTableOptions';
import { useTenantRoute } from '@/account/hook/useTenantRoute';

interface UserDataTableProps {
  data: UserTableRow[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
  filters: MRT_ColumnFiltersState
  setFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>
  sorting: MRT_SortingState
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>
  search: string
  setSearch: (searchText: string) => void
  pagination: MRT_PaginationState
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  rowCount: number
  columnVisibility: MRT_VisibilityState
  setColumnVisibility: React.Dispatch<React.SetStateAction<MRT_VisibilityState>>
}

const UserDataTable: FC<UserDataTableProps> = ({
  data, isLoading, isError, isFetching, refetch, filters, setFilters, sorting, setSorting, pagination, setPagination, search, setSearch,
  rowCount, columnVisibility, setColumnVisibility
}) => {
  const navigate = useNavigate();
  const { tenant } = useTenantRoute();
  const dataTableOptions = useDataTableOptions<UserTableRow>();

  const columns = useMemo<Array<MRT_ColumnDef<UserTableRow>>>(() => {
    return [
      {
        accessorKey: 'id',
        header: 'User ID',
        size: 80
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 200
      },
      {
        accessorKey: 'firstName',
        header: 'First Name',
        size: 120
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        size: 120
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 140
      },
      {
        accessorKey: 'company',
        header: 'Company',
        size: 150
      },
      {
        accessorKey: 'location',
        header: 'Location',
        size: 150
      },
      {
        accessorKey: 'clientId',
        header: 'Membership ID',
        size: 120
      },
      {
        accessorKey: 'lastSignInAt',
        header: 'Last Sign In',
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value ? dayjs(value).format('DD MMM YYYY HH:mm') : '-';
        }
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return value ? dayjs(value).format('DD MMM YYYY') : '-';
        }
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated At',
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return value ? dayjs(value).format('DD MMM YYYY') : '-';
        }
      }
    ];
  }, []);

  const toUserInfoPage = (userId: number) => {
    navigate(generatePageUrl('AccountAccessUserInfo', { tenantCode: tenant?.code ?? '', userId: userId.toString() }));
  };

  const handleClearFilters = () => {
    setFilters([]);
    setPagination({ pageIndex: 0, pageSize: 10 });
    setSearch('');
    setSorting([]);
  };

  const table = useMantineReactTable({
    columns,
    data,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: !!search,
      columnVisibility
    },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    rowCount,
    state: {
      columnFilters: filters,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
      globalFilter: search,
      pagination,
      columnVisibility
    },
    displayColumnDefOptions: { 'mrt-row-actions': { size: 96 } },
    enableRowActions: true,
    positionActionsColumn: 'last',
    globalFilterFn: 'contains',
    renderTopToolbarCustomActions: () => {
      return (
        <UiHStack
          spacing={8}
          flexGrow={1}
          justifyContent={'flex-end'}
          px={8}
        >
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={handleClearFilters}>
            Clear all filters
          </UiButton>
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={refetch}>
            Refresh
          </UiButton>
        </UiHStack>
      );
    },
    renderRowActions: ({ row }) => {
      return (
        <UiHStack
          px={0}
          justifyContent={'flex-start'}
          onClick={() => { return toUserInfoPage(row.original.id); }}
          {...uiStyles.hover}
        >
          <UiIconArrowRight size={'2xl'} color={'primary.500'} />
        </UiHStack>
      );
    },
    ...dataTableOptions
  });

  return <MantineReactTable table={table} />;
};

export default UserDataTable;